// Externe Bibliotheken
import { FormEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import axios from 'axios';

// Interne Module
import * as storage from '@/_helper/storage';
import tracking from '@/_helper/tracking';
import { buildPostRequest } from '@/_helper/apiHelper';
import { checkIsAllowToBookService } from '@/_helper/checkAccess';
import { validateEmail } from '@/_helper/validation';
import BookingData from '@/model/common/BookingData';
import { PageState } from '@/model/common/PageState';
import { PhoneInputFieldData } from '@/model/form/PhoneInput';

// Komponenten
import Header from '@/comp/header/header';
import Footer from '@/comp/footer/Footer';
import PhoneInput from '@/comp/form/phoneInput/PhoneInput';
import StatusPage from '@/comp/statusPage/statusPage';
import Button from '@/comp/button/button';
import BackButton from '@/comp/backButton/backButton';

// Styles
import './emergency-contact.scss';
import { Icon } from '@/comp/icon/icon';

const EmergencyContact = () => {
    const navigate = useNavigate();

    const [pageState, setPageState] = useState<PageState>(PageState.Idle);
    const [alreadyTested, setAlreadyTested] = useState<boolean>(false);
    const [firstNameValidate, setFirstNameValidate] = useState<boolean>(false);
    const [lastNameValidate, setLastNameValidate] = useState<boolean>(false);
    const [mailValidate, setMailValidate] = useState<boolean>(false);
    const [agbChecked, setAgbChecked] = useState<boolean>(false);

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [mail, setMail] = useState('');
    const [phoneData, setPhoneData] = useState<PhoneInputFieldData>();
    const phoneNumber = useMemo(
        () => phoneData && phoneData?.phoneCountryPrefix + phoneData?.phoneAreaCode + phoneData?.phoneNumber,
        [phoneData]
    );

    const firstNameValid = useMemo(() => firstName.length >= 2, [firstName]);
    const lastNameValid = useMemo(() => lastName.length >= 2, [lastName]);
    const phoneValid = useMemo(
        () => phoneData !== undefined && phoneData?.lengthValid === true && phoneData?.isError === false,
        [phoneData]
    );
    const mailValid = useMemo(() => validateEmail(mail), [mail]);

    const textInputsValid = useMemo(
        () => firstNameValid && lastNameValid && phoneValid && mailValid,
        [firstNameValid, lastNameValid, phoneValid, mailValid]
    );
    const formValid = useMemo(() => textInputsValid && agbChecked, [textInputsValid, agbChecked]);

    useEffect(() => {
        document.documentElement.classList.add('redesign');
        document.title = 'Ihre Notfall-Kontaktdaten' + window.$documentTitle;
        if (
            window.$allowEmergencyContact === 'false' ||
            (window.deactivateServices as Array<string>).includes('EmergencyContact')
        ) {
            navigate('/');
            return;
        }
        if (!checkIsAllowToBookService('EmergencyContact')) {
            return;
        }
        tracking('loaded', 'emergencyContact');

        const bookingData: BookingData = storage.get('bookingData') ?? {};
        const emergencyContactData = bookingData.data?.emergencyContactData;
        if (!emergencyContactData || Array.isArray(emergencyContactData)) {
            return;
        }

        if ('name' in emergencyContactData && emergencyContactData.name !== '') {
            const parts = emergencyContactData.name.split(' ');
            if (parts.length >= 2) {
                const lastName = parts.pop() ?? '';
                const firstName = parts.join(' ');
                setFirstName(firstName);
                setLastName(lastName);
            } else {
                setFirstName(emergencyContactData.name);
            }
        }
        if ('mail' in emergencyContactData && emergencyContactData.mail !== '') {
            setMail(emergencyContactData.mail);
        }
        if ('phone' in emergencyContactData && emergencyContactData.phone !== '') {
            const phone = emergencyContactData.phone;
            setPhoneData({
                phoneCountryPrefix: phone.slice(0, 3),
                phoneAreaCode: phone.slice(3, 6),
                phoneNumber: phone.slice(6, phone.length),
                lengthValid: true,
            });
        }
    }, [navigate]);

    const handleCheckboxEvent = (e: KeyboardEvent) => {
        if (e.code === 'Enter' || e.code === 'Space') {
            e.preventDefault();
            setAgbChecked(!agbChecked);
        }
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        setAlreadyTested(true);

        if (!formValid) {
            return;
        }
        setPageState(PageState.Submitting);

        axios(
            buildPostRequest(window.$serviceUrl || '', '/bookingData', {
                requestType: 'bookingData',
                clientId: 'customerarea',
                requestSource: 'AurumTours-Customerarea',
                bookingId: storage.get('bookingNumber'),
                action: 'sendEmergencyContact',
                hash: storage.get('access_token'),
                data: {
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    phone: phoneNumber?.trim(),
                    mail: mail.trim(),
                },
            })
        )
            .then((response) => {
                if (response.status === 200) {
                    if (!window.$affiliate && window.$gtmTracking) {
                        window.$gtmTracking.gtag('event', 'page_success', { page: 'emergencyContact' });
                    }
                    setPageState(PageState.Success);
                    return;
                }
                setPageState(PageState.Error);
            })
            .catch(() => {
                setPageState(PageState.Error);
            })
            .finally(() => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            });
    };

    return (
        <div className="page-emergency-contact">
            <Header />
            {pageState === PageState.Success ? (
                <StatusPage type="success" subPage="emergencyContact" contactName={`${firstName} ${lastName}`} />
            ) : pageState === PageState.Error ? (
                <StatusPage type="error" />
            ) : (
                <div className="content-wrapper">
                    <h1 className="main-headline">Ihr Notfallkontakt</h1>
                    <div className="intro-row">
                        Wir möchten Ihnen auch in unvorhersehbaren Situationen den besten Service bieten. Daher haben
                        Sie hier die Möglichkeit einen Kontakt zu hinterlegen, welchen wir im Notfall verständigen
                        können.
                    </div>
                    <form
                        className={`${pageState === PageState.Submitting ? 'submitting' : ''} ${textInputsValid ? 'valid' : ''}`}
                        onSubmit={handleSubmit}
                        onChange={() => tracking('interaction', 'emergencyContact')}>
                        <section className="card">
                            <div className="form-row flex">
                                <div className="width-expand width-1-2">
                                    <div
                                        className={`input-container${(alreadyTested || firstNameValidate) && !firstNameValid ? ' invalid' : ''}`}>
                                        <label htmlFor="input-first-name">Vorname</label>
                                        <input
                                            type="text"
                                            name="input-first-name"
                                            id="input-first-name"
                                            value={firstName}
                                            placeholder="Vorname"
                                            autoComplete="given-name"
                                            onChange={(e) => setFirstName(e.target.value)}
                                            onBlurCapture={() => setFirstNameValidate(true)}
                                        />
                                        {firstName.length > 0 && (
                                            <span
                                                role="button"
                                                className="reset-trigger"
                                                aria-label="reset"
                                                onClick={() => setFirstName('')}>
                                                <Icon name="close" ariaHidden />
                                            </span>
                                        )}
                                    </div>
                                    {(alreadyTested || firstNameValidate) && !firstNameValid && (
                                        <div className="text-red error">Bitte geben Sie den Vornamen ein.</div>
                                    )}
                                </div>
                                <div className="width-expand width-1-2">
                                    <div
                                        className={`input-container ${(alreadyTested || lastNameValidate) && !lastNameValid ? ' invalid' : ''}`}>
                                        <label htmlFor="input-last-name">Nachname</label>
                                        <input
                                            type="text"
                                            name="input-last-name"
                                            id="input-last-name"
                                            value={lastName}
                                            placeholder="Nachname"
                                            autoComplete="family-name"
                                            onBlurCapture={() => setLastNameValidate(true)}
                                            onChange={(e) => setLastName(e.target.value)}
                                        />
                                        {lastName.length > 0 && (
                                            <span
                                                role="button"
                                                className="reset-trigger"
                                                aria-label="reset"
                                                onClick={() => setLastName('')}>
                                                <Icon name="close" ariaHidden />
                                            </span>
                                        )}
                                    </div>
                                    {(alreadyTested || lastNameValidate) && !lastNameValid && (
                                        <div className="text-red error">Bitte geben Sie den Nachnamen ein.</div>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className={alreadyTested && !phoneValid ? ' invalid' : ''}>
                                    <PhoneInput
                                        label="Handynummer"
                                        phoneData={phoneData}
                                        showDACHOnly={true}
                                        setMobileNumber={(data: PhoneInputFieldData) => setPhoneData(data)}
                                    />
                                    {((alreadyTested && !phoneValid) || phoneData?.isError) && (
                                        <div className="text-red error">
                                            Bitte geben Sie eine gültige Handynummer ein.
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="form-row">
                                <div
                                    className={`input-container ${(alreadyTested || mailValidate) && !mailValid ? 'invalid' : ''}`}>
                                    <label htmlFor="input-mail">E-Mail Adresse</label>
                                    <input
                                        type="email"
                                        name="input-mail"
                                        id="input-mail"
                                        value={mail}
                                        autoComplete="email"
                                        placeholder="max.mustermann@beispielmail.de"
                                        onChange={(e) => setMail(e.target.value)}
                                        onBlurCapture={() => setMailValidate(true)}
                                    />
                                    {mail.length > 0 && (
                                        <span
                                            role="button"
                                            className="reset-trigger"
                                            aria-label="reset"
                                            onClick={() => setMail('')}>
                                            <Icon name="close" ariaHidden />
                                        </span>
                                    )}
                                </div>
                                {(alreadyTested || mailValidate) && !mailValid && (
                                    <div className="text-red error">Bitte geben Sie die E-Mail-Adresse ein.</div>
                                )}
                            </div>
                        </section>
                        <section className="accept-sec">
                            <input
                                id="checkbox-accept"
                                name="checkbox-accept"
                                type="checkbox"
                                checked={agbChecked}
                                onChange={() => setAgbChecked(!agbChecked)}
                                onKeyDown={handleCheckboxEvent}
                            />
                            <div>
                                <label htmlFor="checkbox-accept">
                                    Durch das Absenden Ihrer Daten sind Sie damit einverstanden, dass wir Sie zu
                                    dringlichen Anliegen bezüglich Ihrer gebuchten Reise kontaktieren. Sie sind außerdem
                                    damit einverstanden, dass wir Ihre Daten nach Datenschutzrichtlinien speichern und
                                    verarbeiten. Hier finden Sie unsere{' '}
                                    <NavLink to="/datenschutz" target="_blank">
                                        Datenschutzhinweise
                                    </NavLink>{' '}
                                    und{' '}
                                    <a
                                        href={`${window.$website}/unternehmen/agb`}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        AGB
                                    </a>
                                    .
                                </label>
                                {alreadyTested && !agbChecked && (
                                    <div className="text-red error">
                                        Um das Formular abschicken zu können, müssen Sie den Bedingungen zustimmen.
                                    </div>
                                )}
                            </div>
                        </section>
                        <section className="user-action">
                            <BackButton target="blank" />
                            <Button
                                label={
                                    pageState !== PageState.Submitting
                                        ? 'Kontaktdaten abschicken'
                                        : 'Daten werden übermittelt ...'
                                }
                                type="submit"
                                valid={formValid}
                                validated={pageState === PageState.Submitting}
                            />
                        </section>
                    </form>
                </div>
            )}
            <Footer />
        </div>
    );
};
export default EmergencyContact;
